// Centralized PostHog event helpers for the mock draft funnel.
//
// Mirrors src/utils/recapAnalytics.js: event names and property shapes live
// in one place, posthog-js is imported once, and no PII (no league names,
// no player edits, no team names) ever lands in properties.
//
// PostHog itself is initialized in src/App.js with the project key.

import posthog from "posthog-js";
import { bumpCompletedDraftCount } from "./draftMilestones";
import { activeDialsTag } from "./devDials";

// Analytics must never break the draft loop — swallow capture failures.
//
// Every event carries `dials` when a dev dial cell is active, stamped here
// rather than at each call site so no future event can forget it. It is null in
// production and in stock sessions, so stock properties are unchanged. Dev
// builds are already fully suppressed by the analytics gate (nothing
// transmits), so this is defence in depth for the case where a dial'd session
// somehow reaches a transmitting build.
function safeCapture(event, properties) {
  try {
    const dials = activeDialsTag();
    posthog.capture(event, dials ? { ...properties, dials } : properties);
  } catch {
    /* ignore */
  }
}

// ----------------------------------------------------------------------------
// FUNNEL EVENTS
// ----------------------------------------------------------------------------

// Fired at the real draft-start state transition (startMockDraft), and by
// Quick Sim (which builds+completes a draft without entering the live loop).
// `isSflex` is deliberately the LITERAL superflex-slot test again (aug24). It
// briefly carried `isQbPremiumFormat`, which silently redefined a shipped
// dimension: every 2QB draft would have started reporting `is_sflex: true`, so
// the historical series would have mixed two formats with no way to unmix them
// after the fact. The demand question that predicate answers is a real one, but
// it is not the question this field's name asks, and an analytics field's
// meaning has to stay stable to be worth anything.
//
// `format` is the dimension that actually answers it — '1qb' | 'sflex' | '2qb'
// from the shared util, so it cannot drift from the engine's own classification
// — and `qb_slots`/`sflex_slots` carry the raw roster so a 2QB+SFLEX room
// (labelled 'sflex') is still separable in the data without a fourth label.
//
// `anchor_preset` ('sleeper' | 'yahoo' | 'custom') is DERIVED at both call sites
// from `marketAnchorPresetFor`, the same pure function the MockSetup chip row
// reads. Nothing about the preset is stored on the league, so there is no key
// that could go stale against the numbers: the property always describes the
// anchor actually in force at draft start. A fresh 1QB room reports 'sleeper'
// with nothing typed, because its format default IS the Sleeper pair; SFLEX and
// 2QB report 'custom' until someone picks one.
export function captureMockDraftStarted({ teams, budget, isSflex, format, positions, scoring, anchorPreset }) {
  safeCapture("mock_draft_started", {
    teams: teams ?? null,
    budget: budget ?? null,
    is_sflex: !!isSflex,
    format: format ?? null,
    qb_slots: positions?.QB ?? null,
    sflex_slots: positions?.SFLEX ?? null,
    ppr: scoring?.receptions ?? null,
    pass_td: scoring?.passTD ?? null,
    anchor_preset: anchorPreset ?? null,
  });
}

// Fired when the draft reaches "complete" on any path: the live loop's
// natural end, Simulate-to-End (autoFinishMock), or Quick Sim.
// durationSec is wall-clock from start/resume of the current session;
// null when unknown.
//
// Also bumps the local completed-draft counter here, rather than at the three
// call sites, so the counter and the event can never disagree. The counter is
// local-only UI state, so it increments even when the capture is suppressed
// (localhost, own-device gate, opt-out).
export function captureMockDraftCompleted({ picks, viaSimulate, durationSec }) {
  bumpCompletedDraftCount();
  safeCapture("mock_draft_completed", {
    picks: picks ?? null,
    via_simulate: !!viaSimulate,
    duration_sec: durationSec ?? null,
  });
}

// ----------------------------------------------------------------------------
// NOMINATION FALLBACK
// ----------------------------------------------------------------------------

// Fired whenever the live loop's nominator (pickAiNomination) comes back empty
// and a recovery tier has to fire. The recovery itself is what stops the draft
// freezing; this event is what will eventually explain WHY the ~25-branch
// nomination waterfall returns nothing, which is a separate investigation.
//
// `tier` says how far down the recovery chain we got:
//   nom_pool_top   — waterfall empty, took the best player in the nomination pool
//   available_top  — nomination pool empty too, took the best position-legal player
//   skip_advance   — nothing nominable at all, advanced to the next nominator
//   complete_no_eligible  — ...and no other team had roster room; draft ended
//   complete_skip_lap     — a full lap of skips with no nomination; draft ended
//
// `seq` is the 1-based index of this event within the draft: emission is capped
// per draft (a degenerate late draft could otherwise fire on every pick), so a
// truncated stream stays identifiable rather than looking like it stopped.
export function captureNominationFallback({
  tier, seq, pickNumber, teams, rosterSize, sflex,
  posCounts, nomPoolLen, availableLen, allAvailableLen, isUserSeat,
}) {
  safeCapture("nomination_fallback", {
    tier: tier ?? null,
    seq: seq ?? null,
    pick_number: pickNumber ?? null,
    teams: teams ?? null,
    roster_size: rosterSize ?? null,
    sflex: sflex ?? 0,
    is_user_seat: !!isUserSeat,
    pos_counts: posCounts ?? null,
    nom_pool_len: nomPoolLen ?? null,
    available_len: availableLen ?? null,
    all_available_len: allAvailableLen ?? null,
  });
}

// ----------------------------------------------------------------------------
// NOMINATION WATCHDOG
// ----------------------------------------------------------------------------
// Fires when the draft has sat in phase "nominating" with an unchanging pick
// number for longer than any legitimate nomination takes. That is the freeze
// signature from the Aug-6 investigation: 91 drafts across 16 builds ending on
// a SALE with picks left, median 54s of dead air and a long tail past ten
// minutes.
//
// The five loopIdle components are reported SEPARATELY and never as the derived
// boolean. `loopIdle` being true tells you the loop was gated off but not which
// flag did it, and that is the entire question — four of the five correspond to
// a visible modal the user can close, while mockPaused can be true with nothing
// on screen. A single boolean here would have cost another round trip.
//
// `advanced` records whether the watchdog actually forced the draft on, so a
// log-only fire (loop deliberately idle) is distinguishable from a rescue.
export function captureNominationWatchdog({
  pickNumber, phase, nominatorIndex, nominatorName, isUserSeat, waitedMs, advanced,
  mockPaused, tutorialActive, mockLeaveTarget, settingsOpen, mockShowRecap,
  availableLen, teamsLen, nominee, nomineeAlreadyDrafted,
  currentPlayer, currentBid, currentBidder, goingPhase, activeBidders,
}) {
  safeCapture("nomination_watchdog", {
    pick_number: pickNumber ?? null,
    phase: phase ?? null,
    nominator_index: nominatorIndex ?? null,
    nominator_name: nominatorName ?? null,
    is_user_seat: !!isUserSeat,
    waited_ms: waitedMs ?? null,
    advanced: !!advanced,
    // The five gates, individually.
    idle_mock_paused: !!mockPaused,
    idle_tutorial_active: !!tutorialActive,
    // Value, not just truthiness — which destination was pending is the tell.
    idle_leave_target: mockLeaveTarget ?? null,
    idle_settings_open: !!settingsOpen,
    idle_show_recap: !!mockShowRecap,
    available_len: availableLen ?? null,
    teams_len: teamsLen ?? null,
    nominee: nominee ?? null,
    nominee_already_drafted: !!nomineeAlreadyDrafted,
    // Bidding-phase context. Null in "nominating"; in "bidding" this is what
    // the auction was sitting on when it died.
    current_player: currentPlayer ?? null,
    current_bid: currentBid ?? null,
    current_bidder: currentBidder ?? null,
    going_phase: goingPhase ?? null,
    active_bidders: activeBidders ?? null,
  });
}

// ----------------------------------------------------------------------------
// RESULTS EMAIL
// ----------------------------------------------------------------------------
// The "email me this draft" ask on the results screen. No address is ever
// attached as a property: these events exist to measure volume and failure
// rate, and an email address in PostHog would be PII this project doesn't
// collect anywhere else.

// Fired when a client-side-valid address is submitted, before the POST
// resolves — so requested minus sent is the failure rate.
export function captureResultsEmailRequested() {
  safeCapture("results_email_requested");
}

// Fired on a 200 from /api/email/results. Note this counts accepted requests,
// which includes deduped repeats: the endpoint returns 200 without sending when
// the same address already got this draft.
export function captureResultsEmailSent() {
  safeCapture("results_email_sent");
}

// Fired on a 429 — the per-IP daily cap. Worth watching: mobile carriers CGNAT
// many users behind one address, so a rising rate here more likely means the
// cap is too low than that someone is abusing it.
export function captureResultsEmailRateLimited() {
  safeCapture("results_email_rate_limited");
}

// ----------------------------------------------------------------------------
// PRE-DRAFT TRENDS ASK
// ----------------------------------------------------------------------------
// The "I'll send you what the rooms are doing, the day before you draft" card.
//
// 🔴 SEPARATE EVENTS FROM results_email_* ON PURPOSE. Those three measure a
// different card with a different job, and insights are already built on them.
// Do not fold these in, and do not rename those.
//
// Same no-PII rule as above, and it bites harder here because this ask collects
// a date as well as an address: neither the email nor anything identifying the
// league is ever attached. `days_until_draft` is the whole point — it builds a
// calendar of when real drafts are actually happening, which is what decides
// when a send is worth doing, and it does that without putting a single
// identifying field in PostHog.

// Fired once per results screen, on mount, only when the frequency cap lets the
// card render. This is the denominator: accepted/shown is the conversion rate
// and dismissed/shown is the annoyance rate, and neither means anything without
// a count of impressions that were actually on screen.
export function captureTrendsAskShown() {
  safeCapture("trends_ask_shown");
}

// Fired after the signup POST returns 200 — an accepted signup is one the
// server has, not one the user typed.
//
// `days_until_draft` is null when no date was given (a supported path: the
// address is worth more than the date). It is computed from an instant anchored
// at local noon, so rounding to whole days is exact rather than off-by-one at
// the edges of the day.
export function captureTrendsAskAccepted({ hasDate, daysUntilDraft }) {
  safeCapture("trends_ask_accepted", {
    has_date: !!hasDate,
    days_until_draft: Number.isFinite(daysUntilDraft) ? daysUntilDraft : null,
  });
}

// Fired on "No thanks". `dismiss_count` is the NEW count including this one, so
// a rising share of 2s and 3s says the re-show window is too aggressive.
export function captureTrendsAskDismissed({ dismissCount }) {
  safeCapture("trends_ask_dismissed", {
    dismiss_count: Number.isFinite(dismissCount) ? dismissCount : null,
  });
}

// Fired when the server accepted a signup but writing it to localStorage threw.
//
// 🔴 THIS IS THE SILENT-FAILURE ALARM. The server has the signup and the user
// is shown the confirmation, so nothing looks wrong from either end; what is
// lost is the local record, which means no confirmed card on a later visit and
// the full ask potentially shown again to someone already on the list. A
// client-side failure that follows a SUCCESSFUL server write is exactly the
// class of bug that is invisible without an event for it.
//
// `reason` is the error's name (QuotaExceededError, SecurityError, ...), never
// its message: messages vary by browser and can carry a URL or a key name.
export function captureTrendsAskPersistFailed({ reason }) {
  safeCapture("trends_ask_persist_failed", {
    reason: typeof reason === "string" && reason ? reason : "unknown",
  });
}

// Fired when someone who signed up WITHOUT a date comes back and supplies one,
// from the compact confirmed card.
//
// 🔴 A DISTINCT EVENT, NOT trends_ask_accepted. This measures one specific
// thing: the rate at which the unscheduled bucket converts to a servable date.
// Folding it into accepted would inflate that funnel with people who were
// already signed up and make the conversion it exists to measure invisible.
//
// Same no-PII rule: no address, nothing identifying the league.
// `days_until_draft` is the payoff field again, and it is worth more here than
// anywhere else, because these are the drafts the calendar was missing.
export function captureTrendsDateAdded({ daysUntilDraft }) {
  safeCapture("trends_date_added", {
    days_until_draft: Number.isFinite(daysUntilDraft) ? daysUntilDraft : null,
  });
}
